import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, Search, Users } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Pagination, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { Can } from '@/app/guards/RoleGuard';
import { agentsApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { AgentFormModal } from '../components/AgentFormModal';
import { AgentLoginModal } from '../components/AgentLoginModal';
import type { Agent } from '@/domain/types';

export function AgentsPage(): JSX.Element {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Agent | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [loginTarget, setLoginTarget] = useState<Agent | null>(null);

  const params = { q: search || undefined, page, pageSize: 25, isActive: 'all' as const };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.agents.list(params),
    queryFn: () => agentsApi.list(params),
    placeholderData: (previous) => previous,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('agent.title')}
        actions={
          <Can permission="agents:create">
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t('agent.new')}
            </Button>
          </Can>
        }
      />

      <Card>
        <div className="border-b border-slate-200 p-4">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder={t('common.searchPlaceholder')}
              className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
        </div>

        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={<Users className="h-6 w-6" />} title={t('agent.empty')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('agent.code')}</TH>
                  <TH>{t('agent.name')}</TH>
                  <TH>{t('agent.contactNumber')}</TH>
                  <TH>{t('agent.nic')}</TH>
                  <TH align="center">{t('agent.routes')}</TH>
                  <TH align="center">{t('agent.login')}</TH>
                  <TH align="center">{t('common.status')}</TH>
                  <TH align="right">{t('common.actions')}</TH>
                </TR>
              </THead>

              <TBody>
                {data.rows.map((agent) => (
                  <TR key={agent.id}>
                    <TD className="font-mono text-xs text-slate-500">{agent.code}</TD>
                    <TD className="font-medium text-slate-900">{agent.name}</TD>
                    <TD>{agent.contactNumber ?? '—'}</TD>
                    <TD>{agent.nic ?? '—'}</TD>
                    <TD align="center" numeric>
                      {agent.routeCount}
                    </TD>
                    <TD align="center">
                      {agent.hasLogin ? (
                        <Badge tone="info">{agent.username}</Badge>
                      ) : (
                        // Without a login the agent cannot collect anything.
                        <Badge tone="warning">{t('agent.noLogin')}</Badge>
                      )}
                    </TD>
                    <TD align="center">
                      <Badge tone={agent.isActive ? 'success' : 'neutral'}>
                        {agent.isActive ? t('common.active') : t('common.inactive')}
                      </Badge>
                    </TD>
                    <TD align="right">
                      <Can permission="agents:update">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLoginTarget(agent)}
                            title={agent.hasLogin ? t('agent.resetLogin') : t('agent.createLogin')}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditing(agent);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </Can>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <AgentFormModal isOpen={isFormOpen} onClose={() => setFormOpen(false)} agent={editing} />
      <AgentLoginModal
        isOpen={loginTarget !== null}
        onClose={() => setLoginTarget(null)}
        agent={loginTarget}
      />
    </div>
  );
}
